import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { packingSlipService } from '../../services/api';
import { toast } from 'react-toastify';
import PrintablePackingSlip from './PrintablePackingSlip';

const BulkPrintPackingSlips = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [packingSlips, setPackingSlips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadPackingSlips();
    }, []);

    useEffect(() => {
        // Auto-print when packing slips are loaded
        if (packingSlips.length > 0 && !loading) {
            // Small delay to ensure content is fully rendered
            setTimeout(() => {
                window.print();
            }, 500);
        }
    }, [packingSlips, loading]);

    const loadPackingSlips = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get IDs from query params
            const idsParam = searchParams.get('ids');
            if (!idsParam) {
                setError('No packing slip IDs provided');
                toast.error('No packing slip IDs provided');
                return;
            }

            const ids = idsParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));

            if (ids.length === 0) {
                setError('Invalid packing slip IDs');
                toast.error('Invalid packing slip IDs');
                return;
            }

            // Fetch all packing slips
            const promises = ids.map(id => packingSlipService.getPackingSlip(id));
            const results = await Promise.all(promises);
            const slips = results.map(response => response.data);

            setPackingSlips(slips);
        } catch (error) {
            console.error('Error fetching packing slips:', error);
            setError('Failed to load packing slips');
            toast.error('Failed to load packing slips');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToList = () => {
        navigate('/packing-slips');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading packing slips for printing...</p>
                </div>
            </div>
        );
    }

    if (error || packingSlips.length === 0) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="text-center py-8">
                    <div className="text-red-600 text-xl mb-4">{error || 'No packing slips found'}</div>
                    <button
                        onClick={handleBackToList}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Back to List
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                /* Hide everything except print content when printing */
                @media print {
                    .no-print {
                        display: none !important;
                    }
                }

                .screen-only {
                    display: block;
                }

                .print-only {
                    display: none;
                }

                @media print {
                    .screen-only {
                        display: none !important;
                    }

                    .print-only {
                        display: block !important;
                    }

                    /* Add page breaks between packing slips */
                    .bulk-print-separator {
                        page-break-after: always;
                    }

                    .bulk-print-separator:last-child {
                        page-break-after: auto;
                    }
                }
            `}</style>

            <div className="container mx-auto px-4 py-6">
                {/* Screen-only header with actions */}
                <div className="no-print mb-6 flex justify-between items-center">
                    <button
                        onClick={handleBackToList}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        ← Back to List
                    </button>
                    <div className="flex items-center gap-4">
                        <span className="text-gray-700">
                            {packingSlips.length} packing slip{packingSlips.length !== 1 ? 's' : ''} ready to print
                        </span>
                        <button
                            onClick={() => window.print()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
                        >
                            <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
                            </svg>
                            Print All
                        </button>
                    </div>
                </div>

                {/* Screen preview */}
                <div className="screen-only space-y-6">
                    {packingSlips.map((slip, index) => (
                        <div key={slip.id} className="bg-white rounded-lg shadow-lg p-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">
                                Packing Slip #{slip.packing_slip_number}
                                {slip.production_batch?.batch_number && (
                                    <span className="text-sm text-gray-500 ml-2">
                                        (Batch: {slip.production_batch.batch_number})
                                    </span>
                                )}
                            </h2>
                            <div className="text-sm text-gray-600">
                                <div>Items: {slip.items?.length || 0}</div>
                                <div>Status: {slip.status}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Print version - renders all packing slips */}
                <div className="print-only">
                    {packingSlips.map((slip, index) => (
                        <div key={slip.id} className="bulk-print-separator">
                            <PrintablePackingSlip packingSlip={slip} />
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default BulkPrintPackingSlips;
